import { useEffect, useState, type ReactNode } from 'react'
import { LuFileText } from 'react-icons/lu'
import { FiUsers } from 'react-icons/fi'
import { FiBook } from 'react-icons/fi'
import { LuClipboard } from 'react-icons/lu'
import { PiPercent } from 'react-icons/pi'
import SingleStats from '../../component/singleStats/singleStats'
import './Home.style.scss'
import {
  getStaffDashboard,
  type StaffDashboardActivity,
  type StaffDashboardData,
  type StaffDashboardStat,
} from '../../api/staffDashboard'

const STAT_META: Record<
  string,
  { name: string; color: string; icon: ReactNode }
> = {
  questions: { name: 'Questions', color: '#0BA5EC', icon: <LuFileText /> },
  teachers: { name: 'Teachers', color: '#F04438', icon: <FiUsers /> },
  subjects: { name: 'Subjects', color: '#12B76A', icon: <FiBook /> },
  templates: { name: 'Templates', color: '#F79009', icon: <LuClipboard /> },
  exam_papers: { name: 'Exam papers', color: '#8C88FF', icon: <PiPercent /> },
}

const ACTIVITY_META: Record<string, { color: string; icon: ReactNode }> = {
  question_added: { color: '#0BA5EC', icon: <LuFileText /> },
  template_created: { color: '#F79009', icon: <LuClipboard /> },
  exam_paper_created: { color: '#8C88FF', icon: <PiPercent /> },
}

const formatWhen = (value?: string | null) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    day: 'numeric',
    weekday: 'long',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const percentLabel = (stat: StaffDashboardStat) => {
  if (stat.changePercent === null || stat.changePercent === undefined) return ''
  return `${stat.changePercent}%`
}

const Home = () => {
  const [dashboard, setDashboard] = useState<StaffDashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    getStaffDashboard()
      .then((res) => {
        if (cancelled) return
        if (!res.data?.success || !res.data.data) {
          setDashboard(null)
          setError(res.data?.message || 'Could not load dashboard')
          return
        }
        setDashboard(res.data.data)
      })
      .catch((err) => {
        if (cancelled) return
        setDashboard(null)
        setError(err?.response?.data?.message || 'Could not load dashboard')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [reloadKey])

  const stats = (dashboard?.stats || [])
    .filter((stat) => STAT_META[stat.key])
    .map((stat) => ({
      name: STAT_META[stat.key].name,
      current: String(stat.total),
      increase: String(stat.addedThisMonth),
      up: percentLabel(stat),
      color: STAT_META[stat.key].color,
      icon: STAT_META[stat.key].icon,
    }))

  return (
    <>
      <div className='content dasboard' aria-busy={loading}>
        <h2>Monthly Status Buckets</h2>
        {loading ? (
          <DashboardSkeleton />
        ) : error ? (
          <div className='dashboard_status' role='alert'>
            <p>{error}</p>
            <button type='button' onClick={() => setReloadKey((key) => key + 1)}>
              Try again
            </button>
          </div>
        ) : (
        <>
        <div className='stats'>
          {stats.map((item) => {
            return <SingleStats {...item} key={item?.name} />
          })}
        </div>
        <div className='dashboard_panels'>
          <div className='left'>
            <div className='row'>
              {dashboard?.topTeachers && (
                <div className='teachers'>
                  <div className='title'>Top 5 Teacher</div>
                  <div className='teachers_container'>
                    {dashboard.topTeachers.length === 0 && (
                      <p className='dashboard_empty'>No teachers yet</p>
                    )}
                    {dashboard.topTeachers.map((item) => {
                      return <Singleteacher name={item.name} picture={item.profilePhoto} key={item.id} />
                    })}
                  </div>
                </div>
              )}
              <div className='teachers'>
                <div className='title'>Top 5 Subjects</div>
                <div className='teachers_container'>
                  {(dashboard?.topSubjects || []).length === 0 && (
                    <p className='dashboard_empty'>No subjects yet</p>
                  )}
                  {(dashboard?.topSubjects || []).map((item) => {
                    return (
                      <Singleteacher
                        name={item.title}
                        picture={item.image}
                        description={`${item.questionCount} questions`}
                        fallback="book"
                        key={item.id}
                      />
                    )
                  })}
                </div>
              </div>
            </div>
            <div className='row'>
              <div className='template'>
                <div className='title'>Top 5 Templates</div>
                <div className='template_container'>
                  {(dashboard?.topTemplates || []).length === 0 && (
                    <p className='dashboard_empty'>No templates have been used yet</p>
                  )}
                  {(dashboard?.topTemplates || []).map((item) => {
                    return (
                      <SingleTemplate
                        name={item.name}
                        image={item.thumbnail}
                        subjectTitle={item.subjectTitle}
                        usageCount={item.usageCount}
                        key={item.id}
                      />
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className='activites'>
            <div className='title'>Recent Activites</div>
            <div className='activites_container'>
              {(dashboard?.activity || []).length === 0 && (
                <p className='dashboard_empty'>No recent activity</p>
              )}
              {(dashboard?.activity || []).slice(0, 5).map((item, index) => {
                return <SingleActivites item={item} key={`${item.type}-${item.createdAt}-${index}`} />
              })}
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </>
  )
}

const DashboardSkeleton = () => (
  <div className='dashboard_loading' aria-hidden='true'>
    <div className='stats'>
      {Array.from({ length: 4 }, (_, index) => (
        <div className='dashboard_skeleton dashboard_skeleton--stat' key={index} />
      ))}
    </div>
    <div className='dashboard_panels'>
      <div className='left'>
        <div className='row'>
          <div className='teachers'>
            <div className='title'>Top 5 Subjects</div>
            <div className='teachers_container'>
              {Array.from({ length: 5 }, (_, index) => (
                <div className='dashboard_skeleton dashboard_skeleton--person' key={index} />
              ))}
            </div>
          </div>
        </div>
        <div className='row'>
          <div className='template'>
            <div className='title'>Top 5 Templates</div>
            <div className='template_container'>
              {Array.from({ length: 5 }, (_, index) => (
                <div className='dashboard_skeleton dashboard_skeleton--template' key={index} />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className='activites'>
        <div className='title'>Recent Activites</div>
        <div className='activites_container'>
          {Array.from({ length: 5 }, (_, index) => (
            <div className='dashboard_skeleton dashboard_skeleton--activity' key={index} />
          ))}
        </div>
      </div>
    </div>
  </div>
)

export default Home

const Singleteacher = ({
  name,
  picture,
  description,
  fallback,
}: {
  name: string
  picture?: string | null
  description?: string
  fallback?: 'book'
}) => {
  return (
    <div className='single_teacher'>
      <div className={`picture_container${fallback === 'book' && !picture ? ' picture_container--book' : ''}`}>
        {picture ? <img src={picture} alt={name} /> : fallback === 'book' ? <FiBook /> : null}
      </div>

      <div className='single_teacher__copy'>
        <h3 title={name}>{name}</h3>
        {description && <p title={description}>{description}</p>}
      </div>
    </div>
  )
}

const SingleActivites = ({ item }: { item: StaffDashboardActivity }) => {
  const meta = ACTIVITY_META[item.type] || ACTIVITY_META.question_added
  return (
    <div className='single_activites'>
      <div
        className='sec_title'
        style={{
          color: meta.color,
        }}
      >
        <div
          className='icon'
          style={{
            fontSize: 20,
          }}
        >
          {meta.icon}
        </div>
        <h4>{item.title}</h4>
      </div>
      <p>{item.description}</p>
      <p className='time'>{formatWhen(item.createdAt)}</p>
    </div>
  )
}

const SingleTemplate = ({
  name,
  image,
  subjectTitle,
  usageCount,
}: {
  name: string
  image?: string | null
  subjectTitle?: string
  usageCount?: number
}) => {
  const uses = usageCount || 0
  return (
    <div className='single_template'>
      <div className='image_container'>
        {subjectTitle ? <div className='tag'>{subjectTitle}</div> : null}
        {image ? <img src={image} alt={name} /> : null}
      </div>

      <p>{name}</p>
      <p>{uses === 1 ? 'Used 1 time' : `Used ${uses} times`}</p>
    </div>
  )
}
